import React from 'react'
import { IoPulseOutline, IoArrowUpOutline } from 'react-icons/io5'
import { Widget } from '../Widget'
import { useDCTopPicks } from '../../../swr-config'
import { fmtKickoff } from '../../../utils'
import type { PageId } from '../../BottomNav'

interface DCPick {
  fixture_id: number
  home_team: string
  away_team: string
  home_crest_url?: string
  away_crest_url?: string
  kickoff_utc: string
  market: string
  outcome: string
  probability: number
  implied_prob: number
  edge: number
  best_odds: number
}

interface DixonColesPicksWidgetProps {
  isEditing?: boolean
  onRemove?: () => void
  onNavigate?: (page: PageId) => void
  maxItems?: number
  minEdge?: number
}

export function DixonColesPicksWidget({
  isEditing,
  onRemove,
  onNavigate,
  maxItems = 5,
  minEdge = 5,
}: DixonColesPicksWidgetProps) {
  const { data: picks, error, isLoading } = useDCTopPicks(maxItems, minEdge)

  const handlePickClick = (pick: DCPick) => {
    window.history.pushState(null, '', `#match/${pick.fixture_id}`)
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  }

  return (
    <Widget
      id="dixon"
      title="D&C Top Picks"
      icon={<IoPulseOutline size={18} />}
      linkTo="dixoncoles"
      isEditing={isEditing}
      onRemove={onRemove}
      onNavigate={onNavigate}
      isLoading={isLoading}
      error={error ? 'Failed to load picks' : null}
      skeletonVariant="picks"
    >
      <div className="widget-picks">
        {!picks || picks.length === 0 ? (
          <div className="widget-empty">
            <p>No value picks available</p>
            <button
              className="widget-empty-action"
              onClick={() => onNavigate?.('dixoncoles')}
            >
              Open Dixon-Coles page
            </button>
          </div>
        ) : (
          <ul className="widget-picks-list">
            {picks.map((pick, index) => (
              <li
                key={`${pick.fixture_id}-${pick.market}-${index}`}
                className="widget-pick-item"
                onClick={() => handlePickClick(pick)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handlePickClick(pick)
                  }
                }}
              >
                <div className="widget-pick-match">
                  <span className="widget-pick-teams">
                    {pick.home_team} vs {pick.away_team}
                  </span>
                  <span className="widget-pick-time">
                    {fmtKickoff(pick.kickoff_utc)}
                  </span>
                </div>
                <div className="widget-pick-details">
                  <span className="widget-pick-market">
                    {pick.outcome}
                  </span>
                  <span className="widget-pick-odds">
                    {pick.best_odds.toFixed(2)}
                  </span>
                  <span className="widget-pick-edge">
                    <IoArrowUpOutline size={12} />
                    {pick.edge.toFixed(1)}%
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Widget>
  )
}
